import React, { useEffect, useState, useMemo } from 'react';
import { Box, TextField, Button, Typography, Alert, Stack } from '@mui/material';
import api, { /* fallback */ } from '../../services/api';
import { holidaysSet, parseLocalDate } from '../../utils/businessDays';
import { useToast } from '../ToastProvider';
import { handleApiError, handleApiSuccess } from '../Common/ApiErrorHandler';
import LeaveRequestValidation from './LeaveRequestValidation';

// Lightweight client for leave types with graceful fallback
async function fetchLeaveTypes() {
  try {
    return await api.get('/leave-types');
  } catch (e1) {
    if (e1?.response?.status === 404) {
      try { return await api.get('/LeaveTypes'); } catch (e2) { /* ignore */ }
    }
  }
  console.warn('Leave types endpoint not found. Using empty list.');
  return [];
}

export default function LeaveCreateForm({ onCreated, onDraftChange, draft, onRefresh }) {
  const [types, setTypes] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [requests, setRequests] = useState([]);
  const [form, setForm] = useState({ leaveTypeId: '', startDate: '', endDate: '', days: '', reason: '' });
  const [lastValidated, setLastValidated] = useState({ start:'', days:'' });
  const [serverMismatch, setServerMismatch] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [validation, setValidation] = useState(null);
  const toast = useToast?.() || { success:()=>{}, error:()=>{} };

  useEffect(() => {
    (async () => {
      const t = await fetchLeaveTypes();
      const arr = Array.isArray(t) ? t : [];
      setTypes(arr);
      // Auto-select Paid Leave (AL)
      const paid = arr.find(x => (String(x.code||x.Code).toUpperCase()==='AL') || (String(x.name||x.Name).toLowerCase().includes('paid leave')));
      if (paid) {
        setForm(f => ({ ...f, leaveTypeId: paid.id || paid.Id }));
        // Не презаписвай селекцията от календара (draft). Само обнови leaveTypeId.
        if (onDraftChange) onDraftChange({ ...(draft||{}), leaveTypeId: paid.id || paid.Id });
      } else {
        setError('Paid Leave type (AL) not found.');
      }
      try {
        const hs = await api.get('/holidays');
        setHolidays(Array.isArray(hs) ? hs : []);
        sessionStorage.setItem('hrapp_holidays', JSON.stringify(hs));
      } catch {
        const cached = sessionStorage.getItem('hrapp_holidays');
        if (cached) setHolidays(JSON.parse(cached));
      }
    })();
  }, []);

  // Load existing requests for overlap validation
  useEffect(() => {
    (async () => {
      try { const mine = await api.get('/leave-requests/mine'); setRequests(Array.isArray(mine)?mine:[]); } catch {}
    })();
  }, []);

  const hset = useMemo(() => holidaysSet(holidays), [holidays]);

  const isValidStart = (ymd) => {
    if (!ymd) return false;
    const d = parseLocalDate(ymd);
    const dow = d.getDay(); if (dow===0 || dow===6) return false;
    if (hset.has(ymd)) return false;
    // overlap with pending/approved
    const blocked = (requests||[]).some(r => {
      const status = (r.status||r.Status);
      const kind = status === 'Approved' || status === 'Pending' || status === 'PendingDM' || status === 'PendingBoard';
      if (!kind) return false;
      const s = new Date(r.startDateLocalYmd || r.StartDateLocalYmd || (r.startDate||r.StartDate));
      const e = new Date(r.endDateLocalYmd || r.EndDateLocalYmd || (r.endDate||r.EndDate));
      const ymd2 = parseLocalDate(ymd);
      return ymd2 >= new Date(s.getFullYear(), s.getMonth(), s.getDate()) && ymd2 <= new Date(e.getFullYear(), e.getMonth(), e.getDate());
    });
    return !blocked;
  };


  // Sync incoming draft from parent (calendar selection)
  useEffect(() => {
    if (!draft) return;
    const next = { ...form };
    let changed = false;
    if (draft.startDate && draft.startDate !== form.startDate) { next.startDate = draft.startDate; changed = true; }
    if (draft.days != null && String(draft.days) !== String(form.days)) { next.days = String(draft.days); changed = true; }
    // Keep existing endDate if already computed, otherwise clear it so server preview recalculates
    if (changed) setForm(next);
    if (changed && !draft.endDate) setForm(f => ({ ...f, endDate: '' }));
  }, [draft?.startDate, draft?.days, draft?.endDate]);

  // Server-only preview: fetch end date from API, show it, no local overwrite logic
  useEffect(() => {
    const daysNum = Number(form.days);
    if (form.startDate && daysNum > 0) {
      // Clear previous end while waiting (optional)
      // setForm(f => ({ ...f, endDate: f.endDate }));

      // Background server comparison (do not overwrite UI)
      if (!(lastValidated.start === form.startDate && String(lastValidated.days) === String(daysNum))) {
        const t = setTimeout(() => {
          (async () => {
            try {
              const startUtc = toUtcMidnight(form.startDate);
              const res = await api.get(`/leave-requests/compute-end?startDateUtc=${encodeURIComponent(startUtc)}&businessDays=${daysNum}`);
              const endLocal = res?.endDateLocalYmd || res?.endDateLocal || res?.EndDateLocalYmd;
              const endYmd = String(endLocal || '').substring(0,10);
              setLastValidated({ start: form.startDate, days: daysNum });
              if (endYmd && endYmd !== form.endDate) setForm(f => ({ ...f, endDate: endYmd }));
            } catch { setServerMismatch(false); }
          })();
        }, 300);
        return () => clearTimeout(t);
      }
    }
  }, [form.startDate, form.days, holidays]);

  const handleChange = async (e) => {
    const { name, value } = e.target;

    if (name === 'startDate') {
      // само ако стартът е валиден, разрешаваме дни и превюиране
      const ok = isValidStart(value);
      setForm((f) => ({ ...f, startDate: value, endDate: ok ? f.endDate : '', days: ok ? f.days : '' }));
      if (onDraftChange) onDraftChange({ ...(draft||{}), startDate: value, days: ok ? (draft?.days||form.days) : '' });
      return;
    }

    if (name === 'days') {
      setForm((f) => ({ ...f, days: value }));
      if (onDraftChange) onDraftChange({ ...(draft||{}), startDate: form.startDate, days: value });
      return;
    }

    setForm((f) => ({ ...f, [name]: value }));
  };
  // When start+days valid, fetch preview end
  useEffect(() => {
    const daysNum = Number(form.days);
    if (isValidStart(form.startDate) && daysNum > 0) {
      const t = setTimeout(() => {
        (async () => {
          try {
            const startUtc = toUtcMidnight(form.startDate);
            const res = await api.get(`/leave-requests/compute-end?startDateUtc=${encodeURIComponent(startUtc)}&businessDays=${daysNum}`);
            const endLocal = res?.endDateLocalYmd || res?.endDateLocal || res?.EndDateLocalYmd;
            const endYmd = String(endLocal || '').substring(0,10);
            if (endYmd && endYmd !== form.endDate) setForm(f => ({ ...f, endDate: endYmd }));
          } catch {}
        })();
      }, 250);
      return () => clearTimeout(t);
    }
  }, [form.startDate, form.days]);

  const toUtcMidnight = (dateStr) => {
    // Convert a local yyyy-mm-dd to UTC midnight respecting timezone boundaries
    const [y,m,d] = String(dateStr).split('-').map(Number);
    const local = new Date(y, (m||1)-1, d||1, 0, 0, 0, 0);
    return new Date(Date.UTC(local.getFullYear(), local.getMonth(), local.getDate())).toISOString();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Check validation before submit
    if (validation && !validation.isValid) {
      handleApiError({ response: { data: { errors: validation.errors, warnings: validation.warnings } } });
      return;
    }

    setSubmitting(true); setError(''); setSuccess('');
    try {
      const start = toUtcMidnight(form.startDate);
      const days = Number(form.days);
      // Optional: can call preview but not required for submit
      // await api.get(`/leave-requests/compute-end?startDateUtc=${encodeURIComponent(start)}&businessDays=${days}`);

      const payload = {
        leaveTypeId: Number(form.leaveTypeId),
        startDateUtc: start,
        businessDays: days,
        reason: form.reason || ''
      };
      const res = await api.post('/leave-requests', payload);
      setSuccess(`Leave request submitted (${days} business days)`);
      handleApiSuccess('Заявката за отпуск е подадена успешно');
      if (onCreated) onCreated();
      if (onRefresh) onRefresh();
      // Preserve leaveTypeId; reset dates only
      setForm(f => ({ ...f, startDate: '', endDate: '', days: '', reason: '' }));
    } catch (err) {
      handleApiError(err);
      setError('Неуспешно подаване на заявката');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} sx={{ maxWidth: 900 }}>
      <Stack spacing={2}>
        <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <Typography variant="h6">Create Paid Leave Request</Typography>
        </Box>

        <Box sx={{ display:'grid', gridTemplateColumns:'repeat(3, 1fr) 1fr', gap:2 }}>
          <TextField
            label="Start Date"
            type="date"
            name="startDate"
            value={form.startDate}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
            required
          />

          <TextField
            label="Business Days"
            type="number"
            name="days"
            value={form.days}
            onChange={handleChange}
            inputProps={{ min: 1 }}
            required
            disabled={!form.startDate}
          />

          <TextField
            label="End Date (calculated)"
            type="date"
            name="endDate"
            value={form.endDate}
            InputLabelProps={{ shrink: true }}
            disabled
          />

          <TextField
            label="Reason"
            name="reason"
            value={form.reason}
            onChange={handleChange}
            multiline
            rows={2}
          />
        </Box>

        {/* Real-time validation */}
        {form.leaveTypeId && form.startDate && form.days && (
          <LeaveRequestValidation
            leaveTypeId={Number(form.leaveTypeId)}
            startDate={new Date(form.startDate)}
            businessDays={Number(form.days)}
            onValidationChange={setValidation}
          />
        )}

        <Button
          type="submit"
          variant="contained"
          disabled={submitting || !form.leaveTypeId || (validation && !validation.isValid)}
        >
          Submit
        </Button>
        {/* Inline toast area just under Submit */}
        {error && <Alert severity="error">{error}</Alert>}
        {success && <Alert severity="success">{success}</Alert>}
      {serverMismatch && <Alert severity="warning">Preview differs from server calculation. Final submission will use server’s end date.</Alert>}
      </Stack>
    </Box>
  );
}

